import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeList as List } from "react-window";
import { useTranslation } from "react-i18next";
import { AppTitle } from "@/shared/Components";
import type { SearchResponseItem } from "@/services/api/entities/search";
import type { CSSProperties, FC } from "react";
import { ContactSearchCard } from "../Contacts/Contact";
import { NoteSearchCard } from "../Notes/Note";
import { FIELD_HEIGHT, NOTE_HEIHT, TITLE_HEIGHT } from "../../constants";
import styles from "./styles.module.scss";

type SearchItem = SearchResponseItem & {
  type: "note" | "person" | "noteSeparator" | "personSeparator";
};

type Props = {
  contacts?: SearchItem[];
  notes?: SearchItem[];
  setOpen: (value: boolean) => void;
  textQuery: string;
};

export const SearchListResults: FC<Props> = ({ notes, contacts, setOpen, textQuery }) => {
  const { t } = useTranslation();

  const records = [
    { type: "personSeparator" },
    ...(contacts ?? []),
    { type: "noteSeparator" },
    ...(notes ?? []),
  ];
  const getItemHeight = (index: number) => {
    const recordData = records?.[index] as SearchItem;

    const recordType = recordData.type;

    switch (recordType) {
      case "personSeparator":
      case "noteSeparator":
        return 50;
      case "person":
        return TITLE_HEIGHT + recordData?.fields.length * (FIELD_HEIGHT + 2);
      case "note":
        return NOTE_HEIHT + recordData?.fields.length * (FIELD_HEIGHT + 2);
    }
  };

  const getRow = ({
    record,
    style,
    index,
  }: {
    record: SearchItem;
    style: CSSProperties;
    index: number;
  }) => {
    switch (record.type) {
      case "noteSeparator":
        return (
          <div style={style}>
            <div className={styles["separator"]} />
            <AppTitle className={styles.title} titleType="secondary" level={5}>
              {t("search.notes")}
            </AppTitle>
          </div>
        );
      case "personSeparator":
        return (
          <div style={style}>
            <div className={styles["separator"]} />
            <AppTitle className={styles.title} titleType="secondary" level={5}>
              {t("search.contacts")}
            </AppTitle>
          </div>
        );
      case "note":
        return (
          <div key={index} style={style}>
            <NoteSearchCard textQuery={textQuery} setOpen={setOpen} item={record} key={index} />
          </div>
        );
      case "person":
        return (
          <div key={index} style={style}>
            <ContactSearchCard textQuery={textQuery} setOpen={setOpen} item={record} />
          </div>
        );
    }
  };

  const itemCount = records.length ?? 0;
  return (
    <div className={styles["search-list-results"]}>
      <AutoSizer>
        {({ height, width }) => {
          return (
            <List
              className={styles["list"]}
              width={width}
              layout="vertical"
              itemSize={getItemHeight}
              height={height}
              itemCount={itemCount}
            >
              {(item) => {
                if (!records?.[item.index]) return;
                return getRow({
                  index: item.index,
                  record: records[item.index] as SearchItem,
                  style: item.style,
                });
              }}
            </List>
          );
        }}
      </AutoSizer>
    </div>
  );
};
